import * as React from "react";
import { Box, Flex, IconButton, Input, InputProps, Text } from "@chakra-ui/react";
import { MdRefresh } from "react-icons/md";
import { useI18n } from "src/i18n/I18nContext";

interface CaptchaInputProps extends InputProps {
  leftOperand: number;
  rightOperand: number;
  onRegenerate: () => void;
  invalid?: boolean;
  errorText?: string;
}

export const CaptchaInput = React.forwardRef<HTMLInputElement, CaptchaInputProps>(
  function CaptchaInput(props, ref) {
    const { t } = useI18n();
    const {
      leftOperand,
      rightOperand,
      onRegenerate,
      invalid = false,
      errorText,
      ...inputProps
    } = props;

    return (
      <Box
        bg="gray.50"
        p="4"
        rounded="lg"
        borderWidth="1px"
        borderStyle="dashed"
        borderColor={invalid ? "red.300" : "gray.300"}
      >
        <Flex justify='left' align="center" mb="2">
          <Text fontSize="sm" fontWeight="bold" color="gray.600">
            {t('signup.form.captchaTitle')}
          </Text>
          <IconButton
            aria-label={t('signup.form.captchaRegenerate')}
            variant="ghost"
            size="md"
            color="blue.600"
            onClick={onRegenerate}
          >
            <MdRefresh size={12} />
          </IconButton>
        </Flex>

        <Flex direction={{ base: "column", sm: "row" }} gap="3" align="stretch">
          <Flex
            flex={{ base: "none", sm: "2" }}
            w="full"
            align="center"
            justify="center"
            bg="white"
            borderWidth="1px"
            borderColor="gray.200"
            px="4"
            rounded="md"
            fontWeight="bold"
            fontSize="lg"
            color="blue.600"
            userSelect="none"
            boxShadow="sm"
            h="44px"
          >
            {leftOperand} + {rightOperand} =
          </Flex>

          <Input
            ref={ref}
            flex={{ base: "none", sm: "1" }}
            w="full"
            bg="white"
            placeholder="?"
            fontWeight='bold'
            fontSize="lg"
            type="number"
            borderColor={invalid ? "red.500" : "gray.300"}
            _focus={{ borderColor: invalid ? "red.500" : "blue.500" }}
            h="44px"
            boxShadow="sm"
            {...inputProps}
          />
        </Flex>

        {errorText && (
          <Text fontSize="xs" color="red.500" mt="2">
            {errorText}
          </Text>
        )}
        <Text fontSize="xs" color="gray.500" mt={errorText ? "1" : "2"}>
          {t('signup.form.captchaHint')}
        </Text>
      </Box>
    );
  },
);
